import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDob } from "../../../../utils/helper";
import { getPatientGeneralDetails } from "../../../../services/patient/details";
import { toast } from "react-hot-toast";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import styles from "../DailiesDetailsLayout.module.css";
import Button from "../../../../components/button-v1";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import {
  getPatientNotes,
  createPatientNote,
  updatePatientNote,
  deletePatientNote,
} from "../../../../services/patient/settings";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const schemaValidation = z.object({
  note_text: z.string().min(1, "Note text is required"),
});

const defaultSchema = { note_text: "" };

const DailiesPatientInfo = ({ patientId }) => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing,   setIsEditing]   = useState(false);

  const { register, handleSubmit, setValue, reset } = useForm({
    resolver: zodResolver(schemaValidation),
    defaultValues: defaultSchema,
  });

  // ── Patient details ──────────────────────────────────────────────────────────
  const { data: patient, isLoading } = useQuery(
    ["patient-details", patientId],
    () => getPatientGeneralDetails(patientId),
    { enabled: !!patientId, staleTime: 5 * 60 * 1000 }
  );

  // ── Sticky notes ─────────────────────────────────────────────────────────────
  const { data: patientNotes = [] } = useQuery({
    queryKey: ["patientNotes", patientId],
    queryFn:  () => getPatientNotes(patientId),
    enabled:  !!patientId,
    select:   (data) => data.filter((n) => n.note_type === "sticky_note"),
  });

  const stickyNote = patientNotes?.find((n) => n.note_type === "sticky_note");
  const noteExists = !!stickyNote;

  const createNoteMutation = useMutation(
    (data) => createPatientNote(data, patientId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["patientNotes", patientId] });
        setIsModalOpen(false);
        reset(defaultSchema);
        toast.success("Sticky note added");
      },
      onError: () => toast.error("Failed to add sticky note"),
    }
  );

  const updateNoteMutation = useMutation(
    ({ id, ...data }) => updatePatientNote(id, data, patientId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["patientNotes", patientId] });
        setIsModalOpen(false);
        setIsEditing(false);
        reset(defaultSchema);
        toast.success("Sticky note updated");
      },
      onError: () => toast.error("Failed to update sticky note"),
    }
  );

  const deleteNoteMutation = useMutation(
    (noteId) => deletePatientNote(noteId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["patientNotes", patientId] });
        toast.success("Sticky note deleted");
      },
      onError: () => toast.error("Failed to delete sticky note"),
    }
  );

  const handleSaveNote = (data) => {
    if (isEditing && stickyNote) {
      updateNoteMutation.mutate({ id: stickyNote.id, note_text: data.note_text });
    } else {
      createNoteMutation.mutate({ note_text: data.note_text, note_type: "sticky_note" });
    }
  };

  const handleEdit = () => {
    if (stickyNote) {
      setIsEditing(true);
      setValue("note_text", stickyNote.note_text);
      setIsModalOpen(true);
    }
  };

  const handleDelete = () => {
    if (stickyNote) {
      deleteNoteMutation.mutate(stickyNote.id);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.patientInfoCard}>
        <div className={styles.emptyState}>Loading patient information…</div>
      </div>
    );
  }

  // ── Data mapping — handles BOTH API shapes:
  //    Shape A (patient list): top-level first_name/last_name, no user object
  //    Shape B (detail API):   user: { first_name, last_name, ... }
  // ────────────────────────────────────────────────────────────────────────────
  const user         = patient?.user;
  const provider     = patient?.provider;
  const caseManager  = patient?.case_manager;
  const location     = patient?.location;
  const locationName = patient?.location_name || location?.name || null;
  const tenantName   = patient?.tenant_name || sessionStorage.getItem("tenant_name") || null;

  // Name — prefer user object, fall back to top-level fields
  const patientName = user
    ? `${user.first_name} ${user.last_name}`.trim()
    : patient?.first_name
    ? `${patient.first_name} ${patient.last_name || ""}`.trim()
    : "—";

  // DOB — prefer user.dob, fall back to top-level dob
  const dob = user?.dob || patient?.dob || null;

  // Doctor / Case manager name
  const doctorName = caseManager
    ? `${caseManager.first_name} ${caseManager.last_name || ""}`.trim()
    : null;

  // Account display
  const accountDisplay = (() => {
    const parts = [];
    if (tenantName)   parts.push(tenantName);
    if (locationName) parts.push(locationName);
    return parts.length > 0 ? parts.join(" / ") : "—";
  })();

  // ── Phone numbers ────────────────────────────────────────────────────────────
  // Build a de-duped list of all phone numbers to display:
  //   1. primary_phone (top-level or user.primary_phone)
  //   2. Any entries in contact_numbers (secondary, emergency, etc.)
  // Each entry shows the number + a label badge so the user knows which is which.

  const primaryPhone = user?.primary_phone || patient?.primary_phone || null;
  const contactNumbers = patient?.contact_numbers || user?.contact_numbers || [];

  // Strip everything except digits for dedup comparison
  const digitsOnly = (str) => (str || "").replace(/\D/g, "");

  // Collect all phone lines with labels
  const allPhoneLines = [];
  const seenDigits = new Set();

  if (primaryPhone) {
    const digits = digitsOnly(primaryPhone);
    if (digits) seenDigits.add(digits);
    allPhoneLines.push({ number: primaryPhone, label: "Primary" });
  }

  contactNumbers.forEach((c) => {
    const num = c.number?.trim();
    if (!num) return;
    const digits = digitsOnly(num);
    // Skip if same digits already in list (handles formatting differences)
    if (digits && seenDigits.has(digits)) return;
    if (digits) seenDigits.add(digits);
    // Capitalize label: "secondary" → "Secondary"
    const displayLabel = c.label
      ? c.label.charAt(0).toUpperCase() + c.label.slice(1)
      : "Other";
    allPhoneLines.push({ number: num, label: displayLabel });
  });

  if (allPhoneLines.length === 0) {
    allPhoneLines.push({ number: "—", label: null });
  }

  return (
    <div className={styles.patientInfoCard}>
      <div className={styles.patientInfoLayout}>

        {/* ── Left: label : value rows ── */}
        <div className={styles.patientFieldList}>
          <InfoRow label="Name"    value={patientName}   bold />
          <InfoRow label="DOB"     value={formatDob(dob)} bold />
          <InfoRow label="Doctor"  value={doctorName}     bold />
          <InfoRow label="Account" value={accountDisplay} bold />
        </div>

        {/* ── Right: phone + sticky notes ── */}
        <div className={styles.patientRightCol}>

          {/* Phone block — shows primary + all secondary numbers with labels */}
          <div className={styles.phoneBlock}>
            <span className={styles.phoneLabel}>Phone No. :</span>
            <div className={styles.phoneNumbers}>
              {allPhoneLines.map((line, i) => (
                <div key={i} className={styles.phoneRow}>
                  <span className={styles.phoneValue}>{line.number}</span>
                  {/* {line.label && (
                    <span className={styles.phoneBadge}>{line.label}</span>
                  )} */}
                </div>
              ))}
            </div>
          </div>

          {/* Sticky notes */}
          <div className={styles.stickyNotesBox}>
            <div className={styles.stickyNotesCollapsed}>
              <span className={styles.stickyNotesPlaceholder}>
                {stickyNote?.note_text || "Sticky notes"}
              </span>
              <div style={{ display: "flex", gap: "4px" }}>
                {noteExists ? (
                  <>
                    <Button
                      onClick={handleEdit}
                      size="small"
                      style={{ background: "transparent", border: "none", padding: "4px", boxShadow: "none" }}
                      title="Edit sticky note"
                    >
                      <FaEdit color="#48A6C4" size={14} />
                    </Button>
                    <Button
                      onClick={handleDelete}
                      size="small"
                      style={{ background: "transparent", border: "none", padding: "4px", boxShadow: "none" }}
                      title="Delete sticky note"
                    >
                      <FaTrash color="#ef4444" size={14} />
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => { setIsEditing(false); reset(defaultSchema); setIsModalOpen(true); }}
                    size="small"
                    style={{ background: "transparent", border: "none", padding: "4px", boxShadow: "none" }}
                    title="Add sticky note"
                  >
                    <FaEdit color="#0ea5e9" size={14} />
                  </Button>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Add / Edit modal */}
      <Dialog open={isModalOpen} setOpen={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEditing ? "Edit" : "Add"} Sticky Note</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form onSubmit={handleSubmit(handleSaveNote)}>
              <div style={{ width: "100%" }}>
                <label style={{ fontSize: "12px", fontWeight: "500", color: "#6b7280", display: "block", marginBottom: "4px" }}>
                  Note
                </label>
                <textarea
                  {...register("note_text")}
                  className={styles.stickyNotesTextarea}
                  placeholder="Enter a description for this note"
                  rows={4}
                  maxLength={100}
                  wrap="soft"
                />
                <p style={{ fontSize: "11px", color: "#9ca3af", marginTop: "4px" }}>
                  Max 100 characters are allowed
                </p>
              </div>
            </form>
          </DialogBody>
          <DialogFooter>
            <Button
              onClick={handleSubmit(handleSaveNote)}
              loading={createNoteMutation.isLoading || updateNoteMutation.isLoading}
              disabled={createNoteMutation.isLoading || updateNoteMutation.isLoading}
            >
              {isEditing ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InfoRow = ({ label, value, bold }) => (
  <div className={styles.infoRow}>
    <span className={styles.infoRowLabel}>{label} :</span>
    <span className={bold ? styles.infoRowValueBold : styles.infoRowValue}>
      {value || "—"}
    </span>
  </div>
);

export default DailiesPatientInfo;